/**
 * 水墨山水 — 與青綠共用高度圖，只換畫法。
 *
 * 宣紙是底。高出水面只鋪一層淡墨。緩坡披麻、陡坡斧劈，筆與筆之間留紙。
 * 勾只在凸出的山脊，線可斷。近水處墨淡出。平地不皴。
 */

import { useMemo, useRef } from 'react'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three/webgpu'
import {
  Fn,
  vec2,
  vec3,
  vec4,
  float,
  positionLocal,
  uv,
  smoothstep,
  mix,
  clamp,
  pow,
  fract,
  sin,
  cos,
  dot,
  floor,
  abs,
  max,
  time as tslTime,
  texture,
  uniform,
} from 'three/tsl'
import {
  TERRAIN_WORLD_SIZE,
  TERRAIN_HEIGHT_SCALE,
  WATER_HEIGHT_RATIO,
  WATER_LEVEL,
  HEIGHTMAP_SIZE,
} from '../core/terrainConstants'
import { terrainBuffer } from '../core/terrainBuffer'

const SEGMENTS = 512

const hash21 = Fn(([p_immutable]: [any]) => {
  const p = vec2(p_immutable).toVar()
  const h = dot(p, vec2(127.1, 311.7))
  return fract(sin(h).mul(float(43758.5453)))
})

const valueNoise = Fn(([p_immutable]: [any]) => {
  const p = vec2(p_immutable).toVar()
  const i = floor(p)
  const f = fract(p)
  const u = f.mul(f).mul(float(3).sub(f.mul(float(2))))
  const a = hash21(i)
  const b = hash21(i.add(vec2(1, 0)))
  const c = hash21(i.add(vec2(0, 1)))
  const d = hash21(i.add(vec2(1, 1)))
  return mix(mix(a, b, u.x), mix(c, d, u.x), u.y)
})

function heightTexture() {
  const tex = new THREE.DataTexture(
    terrainBuffer.heightmap,
    HEIGHTMAP_SIZE,
    HEIGHTMAP_SIZE,
    THREE.RGBAFormat,
    THREE.FloatType,
  )
  tex.needsUpdate = true
  tex.wrapS = THREE.ClampToEdgeWrapping
  tex.wrapT = THREE.ClampToEdgeWrapping
  tex.minFilter = THREE.LinearFilter
  tex.magFilter = THREE.LinearFilter
  return tex
}

export function TerrainMeshShuimo() {
  const meshRef = useRef<THREE.Mesh>(null)
  const lastHmapVersion = useRef(0)

  const { heightTex, material } = useMemo(() => {
    const tex = heightTexture()
    const heightScale = uniform(TERRAIN_HEIGHT_SCALE)
    const texelSize = uniform(1.0 / HEIGHTMAP_SIZE)
    const mat = new THREE.MeshStandardNodeMaterial()
    mat.side = THREE.DoubleSide
    mat.roughnessNode = float(1)
    mat.metalnessNode = float(0)

    mat.positionNode = Fn(() => {
      const localPos = positionLocal.toVar()
      const texUV = uv()
      const eps = texelSize.mul(float(2))
      const h00 = texture(tex, texUV.add(vec2(eps.negate(), eps.negate()))).x
      const h10 = texture(tex, texUV.add(vec2(float(0), eps.negate()))).x
      const h20 = texture(tex, texUV.add(vec2(eps, eps.negate()))).x
      const h01 = texture(tex, texUV.add(vec2(eps.negate(), float(0)))).x
      const h11 = texture(tex, texUV).x
      const h21 = texture(tex, texUV.add(vec2(eps, float(0)))).x
      const h02 = texture(tex, texUV.add(vec2(eps.negate(), eps))).x
      const h12 = texture(tex, texUV.add(vec2(float(0), eps))).x
      const h22 = texture(tex, texUV.add(vec2(eps, eps))).x
      const smoothH = h00
        .add(h10.mul(float(2))).add(h20)
        .add(h01.mul(float(2))).add(h11.mul(float(4))).add(h21.mul(float(2)))
        .add(h02).add(h12.mul(float(2))).add(h22)
        .div(float(16))
      return vec3(localPos.x, localPos.y, smoothH.mul(heightScale))
    })()

    const inkColor = Fn(() => {
      const texUV = uv()
      const eps = texelSize
      const normH = texture(tex, texUV).x
      const hR = texture(tex, texUV.add(vec2(eps, float(0)))).x
      const hL = texture(tex, texUV.add(vec2(eps.negate(), float(0)))).x
      const hU = texture(tex, texUV.add(vec2(float(0), eps))).x
      const hD = texture(tex, texUV.add(vec2(float(0), eps.negate()))).x
      const worldStep = float(TERRAIN_WORLD_SIZE).div(float(HEIGHTMAP_SIZE))
      const dhdx = hR.sub(hL).mul(float(TERRAIN_HEIGHT_SCALE)).div(worldStep.mul(float(2)))
      const dhdy = hU.sub(hD).mul(float(TERRAIN_HEIGHT_SCALE)).div(worldStep.mul(float(2)))
      const slopeLen = pow(dhdx.mul(dhdx).add(dhdy.mul(dhdy)), float(0.5))
      const normalY = float(1).div(pow(float(1).add(slopeLen.mul(slopeLen)), float(0.5)))

      const paper = vec3(0.93, 0.90, 0.84)
      const washInk = vec3(0.22, 0.20, 0.18)
      const strokeInk = vec3(0.08, 0.07, 0.06)
      const jiao = vec3(0.02, 0.02, 0.015)
      const waterH = float(WATER_HEIGHT_RATIO)
      // 近水處墨淡出，溶進宣紙，不切出白邊
      const shore = smoothstep(waterH, waterH.add(float(0.035)), normH)

      const hC = texture(tex, texUV).x
      const laplacian = hR.add(hL).add(hU).add(hD).sub(hC.mul(float(4)))
      const ridge = clamp(laplacian.negate().mul(float(90)), float(0), float(1))

      const noiseCoord = texUV.mul(float(HEIGHTMAP_SIZE))
      const slopeSafe = max(slopeLen, float(0.15))
      const dirX = dhdx.div(slopeSafe)
      const dirY = dhdy.div(slopeSafe)
      const along = noiseCoord.x.mul(dirX).add(noiseCoord.y.mul(dirY))
      const across = noiseCoord.x.mul(dirY.mul(float(-1))).add(noiseCoord.y.mul(dirX))

      // 平地不皴。緩坡披麻（長而疏），陡坡斧劈（短筆）
      const flat = smoothstep(float(0.9), float(0.97), normalY)
      const steepFace = float(1).sub(smoothstep(float(0.55), float(0.82), normalY))
      const pima = valueNoise(vec2(along.mul(float(0.12)), across.mul(float(2.8))))
      const axe = valueNoise(vec2(along.mul(float(2.4)), across.mul(float(0.9))))
      const pimaStroke = smoothstep(float(0.58), float(0.74), pima).mul(float(1).sub(steepFace)).mul(float(1).sub(flat))
      const axeStroke = smoothstep(float(0.62), float(0.78), axe).mul(steepFace)
      const cun = max(pimaStroke, axeStroke).mul(shore)

      // 勾只在山脊，並且斷開
      const breakN = valueNoise(noiseCoord.mul(float(0.22)))
      const ridgeLine = smoothstep(float(0.12), float(0.45), ridge)
        .mul(smoothstep(float(0.15), float(0.4), breakN))
        .mul(shore)

      let color: any = paper
      color = mix(color, washInk, shore)
      color = mix(color, strokeInk, cun.mul(float(0.85)))
      color = mix(color, jiao, ridgeLine)
      return color
    })()
    mat.emissiveNode = inkColor
    mat.colorNode = vec4(0, 0, 0, 1)

    return { heightTex: tex, material: mat }
  }, [])

  useFrame(() => {
    if (terrainBuffer.heightmapVersion !== lastHmapVersion.current) {
      lastHmapVersion.current = terrainBuffer.heightmapVersion
      heightTex.image = {
        data: terrainBuffer.heightmap,
        width: HEIGHTMAP_SIZE,
        height: HEIGHTMAP_SIZE,
      }
      heightTex.needsUpdate = true
    }
  })

  return (
    <mesh ref={meshRef} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[TERRAIN_WORLD_SIZE, TERRAIN_WORLD_SIZE, SEGMENTS, SEGMENTS]} />
      <primitive object={material} />
    </mesh>
  )
}

/** 水墨水面：淡墨一層，近岸留白 */
export function WaterPlaneShuimo() {
  const material = useMemo(() => {
    const mat = new THREE.MeshBasicNodeMaterial()
    mat.transparent = true
    mat.depthWrite = false
    mat.side = THREE.DoubleSide
    const time = tslTime
    mat.colorNode = Fn(() => {
      const pos = positionLocal
      const ripple = sin(pos.x.mul(float(0.08)).add(time.mul(float(0.35))))
        .mul(cos(pos.y.mul(float(0.06)).add(time.mul(float(0.22)))))
      const ink = vec3(0.42, 0.44, 0.46)
      const paper = vec3(0.86, 0.85, 0.82)
      const band = smoothstep(float(-0.4), float(0.8), ripple)
      return vec4(mix(paper, ink, float(0.35).add(band.mul(float(0.25)))), float(0.88))
    })()
    return mat
  }, [])

  return (
    <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, WATER_LEVEL, 0]} material={material}>
      <planeGeometry args={[TERRAIN_WORLD_SIZE * 4, TERRAIN_WORLD_SIZE * 4]} />
    </mesh>
  )
}

/** 水墨天：宣紙，近地平線一層淡墨 */
export function ProceduralSkyShuimo() {
  const material = useMemo(() => {
    const mat = new THREE.MeshBasicNodeMaterial()
    mat.side = THREE.BackSide
    mat.depthWrite = false
    mat.fog = false
    mat.colorNode = Fn(() => {
      const p = positionLocal
      const h = p.y
      const paper = vec3(0.94, 0.91, 0.85)
      const mist = vec3(0.72, 0.70, 0.66)
      const wash = smoothstep(float(40), float(-30), h)
      return vec4(mix(paper, mist, wash.mul(float(0.45))), float(1))
    })()
    return mat
  }, [])

  return (
    <mesh material={material}>
      <sphereGeometry args={[1800, 32, 16]} />
    </mesh>
  )
}
